// src/data/progressionDecision.js
//
// RULING 313 (Bob 2026-10-13: "The merits stay with the officers. The platform records the decision and judges nothing.
// yes"). The union's decision not to progress a claim, under the SMART constitution (2024) Article 21B:
//   Section 44 - the member's authority to the union to dispose of his claim "in any manner", subject to appeal, unless he
//   serves written notice to the contrary; the decision furnished in writing within 30 days to the Local Chairperson and the
//   Local's Secretary.
//   Section 75 I - appeals within 90 days: (c) Local Committee of Adjustment -> General Chairperson; (d) General Chairperson
//   -> General Committee of Adjustment (filed with its Secretary); (f) General Committee of Adjustment -> Board of Appeals.
// The decision is recorded (claims/{id}/progressionDecisions, append-only), its dates are computed, and the member and
// the offices are told (functions noticeProgressionDecision). The platform judges nothing.
// Pure; shared with functions\ by scripts\sync-shared-modules.js.
import { lastDayOf } from './timeLimit';   // ruling 314 (forensic audit 2026-10-16) - timeLimit.js is shared too

export const WRITTEN_DAYS = 30;   // Section 44
export const APPEAL_DAYS = 90;    // Section 75 I (c), (d), (f)
export const MAX_REASON = 4000;
export const DECIDER_ROLES = ['local_chair', 'general_chair'];
export const JUDGES_NOTHING = 'The merits stay with the officers. The platform records the decision and judges nothing.';

const s = (v) => (v == null ? '' : String(v)).trim();
const ISO = /^\d{4}-\d{2}-\d{2}$/;

// The calendar date on the union's clock (Eastern).
export function etToday(instant) {
  const d = instant ? new Date(instant) : new Date();
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (e) { return d.toISOString().slice(0, 10); }
}
export function addDays(iso, n) {
  if (!ISO.test(s(iso))) return '';
  const d = new Date(s(iso) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Where the member's appeal goes from a decision by `role` (Section 75 I).
export function appealPath(role) {
  if (role === 'local_chair') return { to: 'the General Chairperson', how: 'filed with the General Chairperson', section: 'Article 21B, Section 75 I (c)' };
  if (role === 'general_chair') return { to: 'the General Committee of Adjustment', how: 'filed with the Secretary of the General Committee, and acted upon not later than its next session', section: 'Article 21B, Section 75 I (d)' };
  return null;
}
const ROLE_WORDS = { local_chair: 'the Local Chairperson (for the Local Committee of Adjustment)', general_chair: 'the General Chairperson' };

// The record an officer writes. `role` is his signed-in role (the rules hold it to his token). The dates are NOT his to
// write: the server computes them from its own clock (datesFor) and adds them to the record.
export function newDecision({ claimId, claim, user, role, reason, serverTime }) {
  const errors = [];
  const c = claim || {};
  if (!s(claimId)) errors.push('No claim is open.');
  if (s(c.status) === 'draft') errors.push('A draft is not with the union; there is nothing to decide.');
  if (!DECIDER_ROLES.includes(role)) errors.push('Only the claim\'s Local Chairperson or General Chairperson records this decision.');
  if (!user || !user.uid) errors.push('You are not signed in.');
  const r = s(reason);
  if (!r) errors.push('Give the reason for the decision. The platform does not weigh it; the member is entitled to know it.');
  if (r.length > MAX_REASON) errors.push('The reason is at most ' + MAX_REASON + ' characters.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, doc: {
    decision: 'not_progressed', decidedBy: user.uid, decidedByName: s(user.name) || s(user.email), decidedRole: role,
    reason: r, decidedAt: serverTime,
  } };
}

// The decision's dates, from the server's clock: decided on (ET), written decision due (Section 44), appeal due (Section 75).
export function datesFor(decidedAtInstant) {
  const on = etToday(decidedAtInstant);
  // Forensic audit 2026-10-16: ruling 314 - the day of the decision is day one, so "within 30 days" ends on day 30 and
  // "within 90 days" on day 90 (timeLimit.lastDayOf). addDays(on, N) put both one day LATE: a member appealing on the
  // date the notice gave him would have been out of time. Section 44 and Section 75 I state no other way of counting.
  return { decidedOn: on, writtenDueOn: lastDayOf(on, WRITTEN_DAYS) || '', appealDueOn: lastDayOf(on, APPEAL_DAYS) || '' };
}

// The notices (functions noticeProgressionDecision writes them; the notification centre reads them by audience).
export function decisionNotices({ claimId, claim, decision }) {
  const c = claim || {}, d = decision || {};
  const label = s(c.claimNumber) || s(c.title) || s(claimId);
  const who = ROLE_WORDS[d.decidedRole] || 'An officer of the union';
  const ap = appealPath(d.decidedRole);
  const common = { kind: 'claim_not_progressed', claimId: s(claimId), committee: s(c.committee || c.generalCommittee), scopeKey: s(c.scopeKey),
    channel: 'in_app', actorName: s(d.decidedByName), actorRole: s(d.decidedRole), readAt: null };
  const memberBody = who + ' (' + s(d.decidedByName) + ') decided on ' + s(d.decidedOn) + ' that the union will not progress this claim. '
    + 'The reason given: "' + s(d.reason) + '". '
    + (ap ? 'You may appeal the decision to ' + ap.to + ', ' + ap.how + ', within ' + APPEAL_DAYS + ' days - by ' + s(d.appealDueOn) + ' (SMART constitution, ' + ap.section + '). ' : '')
    + 'You may instead handle the claim yourself by written notice to the union (Article 21B, Section 44). '
    + 'The carrier\'s time limits on the claim keep running while you decide. ' + JUDGES_NOTHING;
  const officeBody = who + ' (' + s(d.decidedByName) + ') recorded on ' + s(d.decidedOn) + ' that the union will not progress this claim. '
    + 'The written decision is due to the Local Chairperson and the Local\'s Secretary by ' + s(d.writtenDueOn) + ' (Article 21B, Section 44). '
    + 'The member has been told, with his appeal date (' + s(d.appealDueOn) + ').';
  const out = [];
  const memberUid = s(c.claimantUid) || s(c.createdBy);
  if (memberUid) out.push(Object.assign({}, common, { audience: 'claimant', recipientUid: memberUid, title: 'The union will not progress your claim: ' + label, body: memberBody }));
  if (s(c.scopeKey) && !s(c.scopeKey).endsWith('|UNROUTED')) out.push(Object.assign({}, common, { audience: 'local_chair', recipientUid: null, title: 'Decision not to progress: ' + label, body: officeBody }));
  if (common.committee) out.push(Object.assign({}, common, { audience: 'general_chair', recipientUid: null, title: 'Decision not to progress: ' + label, body: officeBody }));
  return out;
}

// What the claim shows (the server writes it onto the claim: progressionDecision).
export function claimMark(decision) {
  const d = decision || {};
  return { decision: 'not_progressed', decidedOn: s(d.decidedOn), decidedRole: s(d.decidedRole), decidedByName: s(d.decidedByName),
    writtenDueOn: s(d.writtenDueOn), appealDueOn: s(d.appealDueOn) };
}
